import { useCallback } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import {
  finalizeConversationDeletion,
  prepareConversationDeletion,
  type DurableConversation,
} from "@/api/conversations";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { purgeChatRuntimeStorage } from "@/desktop/chat-runtime-storage";
import { invalidateListCache } from "./conversation-browser-cache";
import { conversationBrowserErrorMessage } from "./conversation-browser-errors";

interface Options {
  enabled: boolean;
  online: boolean;
  scopeKey: string | null;
  loadFirst: () => Promise<void>;
  mutationInFlightRef: MutableRefObject<boolean>;
  setMutatingId: Dispatch<SetStateAction<string | null>>;
  setSelected: Dispatch<SetStateAction<DurableConversation | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
}

const PURGE_RETRY_DELAYS_MS = [500, 1000, 2000, 4000];

async function purgeWhenReleased(
  targets: Parameters<typeof purgeChatRuntimeStorage>[0],
): Promise<void> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await purgeChatRuntimeStorage(targets);
      return;
    } catch (error) {
      const delay = PURGE_RETRY_DELAYS_MS[attempt];
      if (delay === undefined || !String(error).includes("still active")) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

export function useConversationDelete(options: Options) {
  const {
    enabled,
    online,
    scopeKey,
    loadFirst,
    mutationInFlightRef,
    setMutatingId,
    setSelected,
    setError,
  } = options;

  return useCallback(
    async (conversation: DurableConversation): Promise<boolean> => {
      if (
        !enabled ||
        !online ||
        mutationInFlightRef.current ||
        !(conversation.lifecycle?.actions.delete.allowed ?? false)
      ) {
        return false;
      }
      mutationInFlightRef.current = true;
      setMutatingId(conversation.id);
      setError(null);
      const isCurrentScope = captureDesktopChatAuthScopeGuard();
      try {
        const requestedDeletionId = crypto.randomUUID();
        const prepared = await prepareConversationDeletion(
          conversation.id,
          requestedDeletionId,
        );
        if (!isCurrentScope()) return false;
        if (prepared.state === "purge_required") {
          await purgeWhenReleased(prepared.runtime_targets);
          if (!isCurrentScope()) return false;
          await finalizeConversationDeletion(
            conversation.id,
            prepared.deletion_id,
          );
        }
        if (!isCurrentScope()) return false;
        if (scopeKey) invalidateListCache(scopeKey);
        setSelected(null);
        await loadFirst();
        return isCurrentScope();
      } catch (error) {
        if (isCurrentScope()) setError(conversationBrowserErrorMessage(error));
        return false;
      } finally {
        mutationInFlightRef.current = false;
        if (isCurrentScope()) setMutatingId(null);
      }
    },
    [
      enabled,
      loadFirst,
      mutationInFlightRef,
      online,
      scopeKey,
      setError,
      setMutatingId,
      setSelected,
    ],
  );
}
